/**
 * Voluum-style lander tags: chips + type/select from GET /tag.
 * Inline list (no portaled Popover) so it works inside a Radix Dialog drawer.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import { getVoluumLanderTags } from '@/features/flow-optimizer/services/voluum-tags-api';

import {
  hasTagInsensitive,
  joinTagsCsv,
  parseTagsCsv,
  tagMatchesNeedle,
} from './lander-tags-helpers';
import LanderSelectedTagChips from './LanderSelectedTagChips';
import LanderTagSuggestionsList from './LanderTagSuggestionsList';
import { useDismissOnOutsidePointer } from './use-dismiss-on-outside-pointer';

export interface LanderTagsFieldProps {
  tagsCsv: string;
  onTagsCsvChange: (csv: string) => void;
  disabled?: boolean;
}

const LanderTagsField: FC<LanderTagsFieldProps> = ({
  tagsCsv,
  onTagsCsvChange,
  disabled,
}) => {
  const selected = useMemo(() => parseTagsCsv(tagsCsv), [tagsCsv]);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');

  const close = useCallback(() => setOpen(false), []);
  useDismissOnOutsidePointer(rootRef, open && !disabled, close);

  const { data: options = [], isFetching, isError } = useQuery({
    queryKey: ['voluum-lander-tags'],
    queryFn: () => getVoluumLanderTags(),
    enabled: open && !disabled,
    staleTime: 60_000,
  });

  const suggestions = useMemo(
    () =>
      options
        .filter((t) => tagMatchesNeedle(t, input))
        .filter((t) => !hasTagInsensitive(selected, t)),
    [options, selected, input],
  );

  const addTag = (raw: string) => {
    const t = raw.trim();
    if (!t || hasTagInsensitive(selected, t)) return;
    onTagsCsvChange(joinTagsCsv([...selected, t]));
    setInput('');
  };

  const removeTag = (t: string) => {
    onTagsCsvChange(joinTagsCsv(selected.filter((s) => s !== t)));
  };

  const listOpen = open && !disabled;
  const rowCls = cn(
    'flex min-w-0 rounded-md border border-input bg-background ring-offset-background',
    'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
  );

  return (
    <div className="space-y-1.5">
      <LanderSelectedTagChips
        tags={selected}
        disabled={disabled}
        onRemove={removeTag}
      />
      <div ref={rootRef} className="relative min-w-0">
        <div className={rowCls}>
          <input
            id="lander-tags"
            type="text"
            value={input}
            disabled={disabled}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (!input.trim() && suggestions[0]) addTag(suggestions[0]);
              else addTag(input);
            }}
            placeholder="Type or select tags from list"
            autoComplete="off"
            aria-expanded={listOpen}
            aria-controls="lander-tags-listbox"
            role="combobox"
            className={cn(
              'h-8 min-w-0 flex-1 border-0 bg-transparent px-3 py-1 text-sm outline-none',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          <button
            type="button"
            disabled={disabled}
            aria-label={listOpen ? 'Hide tag suggestions' : 'Show tag suggestions'}
            className={cn(
              'flex h-8 shrink-0 items-center border-l border-input px-2',
              'text-muted-foreground hover:bg-muted/80 disabled:opacity-50',
            )}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronsUpDown className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {listOpen ? (
          <div
            id="lander-tags-listbox"
            role="listbox"
            className={cn(
              'absolute left-0 right-0 top-full z-[70] mt-1 max-h-[min(50vh,360px)]',
              'overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md',
            )}
          >
            <LanderTagSuggestionsList
              isError={isError}
              isFetching={isFetching}
              options={options}
              suggestions={suggestions}
              onPick={(t) => {
                addTag(t);
                setOpen(false);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LanderTagsField;
